import { useEffect, forwardRef, useImperativeHandle, useRef } from 'react'
import {
  Alert,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Typography,
} from '@mui/material'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import { TableRow, TableCell, TableHeader } from '@tiptap/extension-table'
import { BorderlessTable } from '../extensions/BorderlessTable'
import { ResizableImage, ImageBubbleMenu } from '../extensions/ResizableImage'
import { useAdventureEditorStore, selectCanEditTitle } from '../../../stores/adventureEditorStore'
import {
  AdventureMainCategory,
  ADVENTURE_MAIN_CATEGORY_LABELS,
} from '../../../types/adventure-editor.types'
import { TipTapToolbar } from './TipTapToolbar'
import { useCollaborativeAdventureEditor } from '@hooks/useCollaborativeAdventureEditor'
import { usePublishedPermissions } from '../../../hooks/usePublishedPermissions'
import { WithFieldChangeIndicator } from '../FieldChangeIndicator'
import { PublishReadinessChecklist } from '../PublishReadinessChecklist'
import '../styles/DescriptionPanel.css'

// ====================
// TYPES
// ====================

export interface DescriptionPanelRef {
  getEditorContentAsync: () => Promise<string>
  isEditorLoaded: () => boolean
}

interface DescriptionPanelProps {}

// ====================
// COMPONENT
// ====================

export const DescriptionPanel = forwardRef<DescriptionPanelRef, DescriptionPanelProps>(
  (props, ref) => {
    const generalInfo = useAdventureEditorStore((state) => state.generalInfo)
    const canEditTitle = useAdventureEditorStore(selectCanEditTitle)

    // Published permissions - restricts editing based on adventure status and user role
    const {
      isPublished,
      isModerator,
      canEditQuickDescription,
      canEditMainCategory,
      canEditFullDescription,
    } = usePublishedPermissions()

    // Collaborative editing hook - syncs changes with other editors
    const { updateGeneralInfoField, isApplyingRemoteChange } = useCollaborativeAdventureEditor()

    // Track if we're updating from remote to prevent re-broadcasting
    const isRemoteUpdateRef = useRef(false)

    // Initialize TipTap editor
    const editor = useEditor(
      {
        extensions: [
          StarterKit.configure({
            // Disable Link in StarterKit since we configure it separately
            link: false,
          }),
          TextAlign.configure({
            types: ['heading', 'paragraph'],
          }),
          BorderlessTable.configure({ resizable: true }),
          TableRow,
          TableCell,
          TableHeader,
          ResizableImage.configure({
            inline: false,
            allowBase64: true,
          }),
          Link.configure({
            openOnClick: false,
          }),
        ],
        content: generalInfo.fullDescription || '',
        editable: canEditFullDescription,
        onUpdate: ({ editor }) => {
          // Don't broadcast if this update came from a remote change
          if (isRemoteUpdateRef.current) return

          const html = editor.getHTML()
          updateGeneralInfoField('fullDescription', html)
        },
      },
      [canEditFullDescription] // Re-create editor when editability changes
    )

    // Update editor editability when permissions change
    useEffect(() => {
      if (editor) {
        editor.setEditable(canEditFullDescription)
      }
    }, [editor, canEditFullDescription])

    // Update editor content when generalInfo changes (e.g., when loading an adventure or remote update)
    useEffect(() => {
      if (editor && generalInfo.fullDescription !== editor.getHTML()) {
        // Mark that this is a remote update to prevent re-broadcasting
        isRemoteUpdateRef.current = true
        editor.commands.setContent(generalInfo.fullDescription || '')
        // Reset after microtask
        queueMicrotask(() => {
          isRemoteUpdateRef.current = false
        })
      }
    }, [generalInfo.fullDescription, editor])

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      getEditorContentAsync: async () => {
        if (editor) {
          return editor.getHTML()
        }
        return ''
      },
      isEditorLoaded: () => editor !== null,
    }))

    // Cleanup
    useEffect(() => {
      return () => {
        editor?.destroy()
      }
    }, [editor])

    const handleFieldChange = (field: keyof typeof generalInfo, value: any) => {
      updateGeneralInfoField(field, value)
    }

    const characterCount = generalInfo.quickDescription?.length || 0

    return (
      <Paper sx={{ p: '15px', display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
        <Typography variant="h5" gutterBottom>
          Adventure Description
        </Typography>

        {/* Publish Readiness Checklist */}
        <Box sx={{ mb: 3 }}>
          <PublishReadinessChecklist defaultCollapsed={false} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 0, overflow: 'visible' }}>
          {/* Adventure Title */}
          <WithFieldChangeIndicator field="title" offset={{ top: 8, right: 8 }}>
            <TextField
              fullWidth
              label="Adventure Title"
              value={generalInfo.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              variant="outlined"
              required
              inputProps={{ maxLength: 100 }}
              disabled={!canEditTitle}
              helperText={
                canEditTitle
                  ? 'Adventure title (locked once published)'
                  : 'Title is locked after publishing'
              }
              error={!generalInfo.title || generalInfo.title.length < 3}
            />
          </WithFieldChangeIndicator>

          {/* Quick Description */}
          <WithFieldChangeIndicator field="quickDescription" offset={{ top: 8, right: 8 }}>
            <TextField
              fullWidth
              label="Quick Description"
              value={generalInfo.quickDescription || ''}
              onChange={(e) => handleFieldChange('quickDescription', e.target.value)}
              variant="outlined"
              multiline
              rows={3}
              inputProps={{ maxLength: 180 }}
              disabled={!canEditQuickDescription}
              helperText={
                canEditQuickDescription
                  ? `Used for glyph hover or list previews (max 180 characters) - ${characterCount}/180`
                  : 'You do not have permission to edit this field'
              }
            />
          </WithFieldChangeIndicator>

          {/* Main Category */}
          <WithFieldChangeIndicator field="mainCategory" offset={{ top: 8, right: 32 }}>
            <FormControl fullWidth variant="outlined" required disabled={!canEditMainCategory}>
              <InputLabel>Adventure Category</InputLabel>
              <Select
                value={generalInfo.mainCategory}
                onChange={(e) =>
                  handleFieldChange('mainCategory', Number(e.target.value) as AdventureMainCategory)
                }
                label="Adventure Category"
              >
                {Object.entries(ADVENTURE_MAIN_CATEGORY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Category is set when the adventure is created and cannot be changed
              </FormHelperText>
            </FormControl>
          </WithFieldChangeIndicator>

          {/* Full Description - Rich Text Editor */}
          <WithFieldChangeIndicator field="fullDescription" offset={{ top: 0, right: 0 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
              Full Description
            </Typography>
            {isPublished && canEditFullDescription && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You can edit this field because you are a System {isModerator ? 'Moderator' : 'Admin'}.
              </Alert>
            )}
            {isPublished && !canEditFullDescription && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This field is locked because the adventure is published. Only System Admins and Moderators can edit it.
              </Alert>
            )}
            <Box className="rich-editor-container" sx={{
              overflow: 'visible',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              color: 'text.primary',
              '&:focus-within': {
                borderColor: 'primary.main',
                borderWidth: 2,
              },
            }}>
              {editor && canEditFullDescription && <TipTapToolbar editor={editor} />}
              <EditorContent editor={editor} className="tiptap-editor" />
              {editor && canEditFullDescription && <ImageBubbleMenu editor={editor} />}
            </Box>
          </WithFieldChangeIndicator>
        </Box>
      </Paper>
    )
  }
)

DescriptionPanel.displayName = 'DescriptionPanel'
